import { CommonModule } from '@angular/common';
import { Component, computed, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { filter, map, shareReplay } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { ContractorsService } from 'src/app/services/contractors.service';
import { SitesService } from 'src/app/services/sites.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { ModalShellComponent } from 'src/app/ui/modal-shell/modal-shell.component';
import { Roles, roles } from 'src/app/utility/role';
import { capitalizeFirstLetter } from 'src/app/utility/string';

@Component({
  selector: 'app-invite-user-dialog',
  templateUrl: './invite-user.dialog.html',
  styleUrls: ['./invite-user.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    ModalShellComponent,
    FormsModule,
  ],
})
export class InviteUserDialogComponent implements OnInit {

  private sites$ = this.sitesService.getAdminList();

  formGroup: FormGroup;

  dummy = input(false);
  role = input<Roles>();
  site = input<string>();

  isUser = signal(true);
  roles$ = this.usersService.getCurrentUser().pipe(
    map((user) => {
      if (user.role === Roles.superAdmin) {
        return [...roles, Roles.partner];
      } else if (user.role === Roles.partner) {
        this.formGroup.get('role').setValue(Roles.partner);
        return [Roles.partner];
      } else {
        return [...roles, Roles.supervisor];
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  allWorksites$ = this.sites$.pipe(
    filter((sites) => sites.length > 0),
    map((sites) => sites.map((site) => {
      site.clientName = capitalizeFirstLetter(site.client);
      return { name: site.name, clientName: site.clientName, guid: site.guid };
    })),
    map((sites) => sites.sort(fieldSorter(['clientName', 'name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  contractors$ = this.contractorsService.getList().pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  userRoleS = computed(() => this.usersService.role());

  constructor(
    private contractorsService: ContractorsService,
    private formBuilder: FormBuilder,
    private _modal: ModalController,
    private sitesService: SitesService,
    private usersService: UserSessionService,
  ) { }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      displayName: ['', Validators.required],
      role: [this.role(), Validators.required],
      email: ['', [Validators.required, Validators.email]],
      worksites: [this.site() ? [{ guid: this.site() }] : []],
      phone: [''],
      address: this.formBuilder.group({
        street: [''],
        postalCode: [''],
        city: [''],
      }),
      contractors: [[]],
      rights: this.formBuilder.group({
        officer: [false],
        calendar: [false],
        supervisor: [false],
      }),
      company: ['', this.role() === Roles.calendar ? Validators.required : Validators.nullValidator],
    });
  }

  onSave() {
    if (this.isUser()) {
      const user = this.formGroup.value;
      if (![Roles.partner].includes(user.role)) {
        if (user.role === Roles.supervisor) {
          user.rights.supervisor = true;
          user.role = Roles.admin;
        }
        if (this.userRoleS() !== Roles.superAdmin) {
          user.contractors = [Roles.manager].includes(this.userRoleS())
            ? this.usersService.currentUserS().contractors : [this.contractorsService.contractorS()?.guid];
        }
        user.worksites = user.worksites?.map((it) => it.guid) ?? [];
      }
      this._modal.dismiss(user);
    } else {
      const user = this.formGroup.value;
      if (![Roles.partner].includes(user.role)) {
        user.contractors = [this.contractorsService.contractorS()?.guid];
        user.worksites = user.worksites?.map((it) => it.guid) ?? [];
      }
      user.disabled = true;
      this._modal.dismiss(user);
    }
  }

  dismiss() {
    this._modal.dismiss();
  }
}
